import React, { useRef, useState } from 'react';

const VisorHistoriaClinica = ({ patient, onCancel }) => {
    const printRef = useRef(null);

    // Initial state with patient data and empty fields
    const [localData, setLocalData] = useState({
        nombres: `${patient?.first_name || ''} ${patient?.last_name || ''}`.trim(),
        cedula: patient?.cedula || '',
        representante: patient?.representative || '',
        telefono: patient?.phone || '',
        dob: patient?.birth_date ? patient.birth_date.split('-').reverse().join('/') : '',
        edad: (patient?.age !== undefined && patient?.age !== null) ? `${patient.age} años` : '',
        estadoCivil: '',
        sexo: patient?.gender || '',
        profesion: patient?.occupation || '',
        direccionTrabajo: '',
        direccionDomicilio: patient?.address || '',
        correo: patient?.email || '',
        observaciones: ''
    });

    // Calculate age automatically once if possible
    React.useEffect(() => {
        if (patient?.birth_date && !localData.edad) {
            const birthDate = new Date(patient.birth_date);
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const m = today.getMonth() - birthDate.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                age--;
            }
            setLocalData(prev => ({ ...prev, edad: `${age} años` }));
        }
    }, [patient]);

    const handleChange = (field) => (e) => {
        setLocalData(prev => ({ ...prev, [field]: e.target.value }));
    };

    const handleSexoChange = (s) => () => {
        setLocalData(prev => ({ ...prev, sexo: s }));
    };

    const handlePrint = async () => {
        const html2pdf = (await import('html2pdf.js')).default;
        
        const input = document.getElementById("print-area");
        
        const opt = {
            margin: [5, 5, 5, 5],
            filename: `Historia_Clinica_${localData.nombres}.pdf`,
            image: { type: 'jpeg', quality: 1 },
            html2canvas: { scale: 2, useCORS: true, scrollY: 0, windowWidth: 1200 },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
            pagebreak: { mode: ['css', 'legacy'], avoid: 'tr' }
        };
        
        try {
            await html2pdf().set(opt).from(input).save();
        } catch (error) {
            console.error("Error generando PDF", error);
        }
    };

    if (!patient) return null;

    // Theme color based on the provided image
    const themeColor = '#a1917f'; 
    const bgColor = '#ebebeb'; // Light gray for header

    // Styles
    const pageStyle = {
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        backgroundColor: 'white',
        width: '210mm',
        minHeight: '297mm',
        margin: '0 auto',
        paddingBottom: '20mm',
        boxSizing: 'border-box',
        position: 'relative'
    };

    const headerContainerStyle = {
        backgroundColor: bgColor,
        width: '100%',
        padding: '20px 0',
        textAlign: 'center',
        borderBottom: `2px solid ${themeColor}`
    };

    const logoStyle = {
        maxHeight: '80px',
        objectFit: 'contain'
    };

    const sectionTitleContainerStyle = {
        position: 'relative',
        textAlign: 'center',
        marginTop: '-25px', // Overlap the border
        marginBottom: '15px'
    };

    const sectionTitleStyle = {
        display: 'inline-block',
        backgroundColor: 'white',
        color: themeColor,
        border: `2px solid ${themeColor}`,
        borderRadius: '25px',
        padding: '8px 40px',
        fontSize: '22px',
        fontWeight: '900',
        letterSpacing: '2px',
        margin: 0
    };

    const subSectionTitleStyle = {
        display: 'inline-block',
        color: themeColor,
        border: `2px solid ${themeColor}`,
        borderRadius: '20px',
        padding: '4px 20px',
        fontSize: '14px',
        fontWeight: 'bold',
        letterSpacing: '1px',
        marginTop: '10px',
        marginBottom: '20px'
    };

    const contentStyle = {
        padding: '0 40px'
    };

    const fieldGroupStyle = {
        marginBottom: '12px'
    };

    const labelStyle = {
        color: themeColor,
        fontSize: '12px',
        fontWeight: 'bold',
        fontStyle: 'italic',
        marginBottom: '4px',
        display: 'block',
        paddingLeft: '10px'
    };

    const inputContainerStyle = {
        width: '100%',
        border: `2px solid ${themeColor}`,
        borderRadius: '20px',
        padding: '0 15px',
        backgroundColor: 'white',
        boxSizing: 'border-box',
        minHeight: '38px',
        display: 'flex',
        alignItems: 'center'
    };

    const editableInputStyle = {
        width: '100%',
        border: 'none',
        outline: 'none',
        backgroundColor: 'transparent',
        fontSize: '14px',
        color: '#333',
        fontFamily: 'inherit',
        padding: '8px 0'
    };

    const textAreaContainerStyle = {
        ...inputContainerStyle,
        minHeight: '80px',
        backgroundColor: '#f2f2f2', // Light gray background for observations as in image
        alignItems: 'flex-start',
        padding: '8px 15px'
    };

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 10000, overflowY: 'auto' }}>
            
            {/* Control Bar (Not printed) */}
            <div className="d-print-none sticky-top" style={{ backgroundColor: '#333', padding: '15px', display: 'flex', justifyContent: 'center', gap: '15px', boxShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>
                <button onClick={handlePrint} className="btn btn-light fw-bold px-4 rounded-pill">
                    <i className="bi bi-file-earmark-pdf-fill me-2"></i> Descargar PDF
                </button>
                <button onClick={onCancel} className="btn btn-danger fw-bold px-4 rounded-pill">
                    <i className="bi bi-x-circle-fill me-2"></i> Cerrar
                </button>
            </div>

            {/* Print Area */}
            <style>
                {`
                    @media print {
                        body * {
                            visibility: hidden;
                        }
                        #print-area, #print-area * {
                            visibility: visible !important;
                        }
                        #print-area {
                            position: absolute;
                            left: 0;
                            top: 0;
                            width: 210mm !important;
                            background-color: white !important;
                            margin: 0 !important;
                            padding: 0 !important;
                            box-shadow: none !important;
                        }
                        @page {
                            size: A4;
                            margin: 0;
                        }
                    }
                `}
            </style>

            <div id="print-area" ref={printRef} style={{ ...pageStyle, margin: '20px auto', boxShadow: '0 0 20px rgba(0,0,0,0.2)' }}>
                
                {/* Header with Logo */}
                <div style={headerContainerStyle}>
                    <img src="/biodentality.png" alt="Biodentality" style={logoStyle} />
                </div>

                {/* Title */}
                <div style={sectionTitleContainerStyle}>
                    <h1 style={sectionTitleStyle}>HISTORIA CLÍNICA</h1>
                </div>

                <div style={{ textAlign: 'center' }}>
                    <h2 style={subSectionTitleStyle}>INFORMACIÓN PERSONAL</h2>
                </div>

                {/* Form Fields */}
                <div style={contentStyle}>
                    
                    {/* Row 1 */}
                    <div style={{ display: 'flex', gap: '15px', ...fieldGroupStyle }}>
                        <div style={{ flex: '6' }}>
                            <label style={labelStyle}>NOMBRES Y APELLIDOS</label>
                            <div style={inputContainerStyle}>
                                <input type="text" value={localData.nombres} onChange={handleChange('nombres')} style={editableInputStyle} />
                            </div>
                        </div>
                        <div style={{ flex: '4' }}>
                            <label style={labelStyle}>CÉDULA DE IDENTIDAD</label>
                            <div style={inputContainerStyle}>
                                <input type="text" value={localData.cedula} onChange={handleChange('cedula')} style={editableInputStyle} />
                            </div>
                        </div>
                    </div>

                    {/* Row 2 */}
                    <div style={{ display: 'flex', gap: '15px', ...fieldGroupStyle }}>
                        <div style={{ flex: '6' }}>
                            <label style={labelStyle}>REPRESENTANTE</label>
                            <div style={inputContainerStyle}>
                                <input type="text" value={localData.representante} onChange={handleChange('representante')} style={editableInputStyle} />
                            </div>
                        </div>
                        <div style={{ flex: '4' }}>
                            <label style={labelStyle}>TELÉFONO</label>
                            <div style={inputContainerStyle}>
                                <input type="text" value={localData.telefono} onChange={handleChange('telefono')} style={editableInputStyle} />
                            </div>
                        </div>
                    </div>

                    {/* Row 3 */}
                    <div style={{ display: 'flex', gap: '15px', ...fieldGroupStyle }}>
                        <div style={{ flex: '4' }}>
                            <label style={labelStyle}>FECHA DE NACIMIENTO</label>
                            <div style={{...inputContainerStyle, color: localData.dob ? '#333' : '#a89d8f', justifyContent: 'center'}}>
                                <input type="text" value={localData.dob} onChange={handleChange('dob')} style={{...editableInputStyle, textAlign: 'center'}} placeholder="____/_________/______" />
                            </div>
                        </div>
                        <div style={{ flex: '3' }}>
                            <label style={labelStyle}>EDAD</label>
                            <div style={inputContainerStyle}>
                                <input type="text" value={localData.edad} onChange={handleChange('edad')} style={editableInputStyle} />
                            </div>
                        </div>
                        <div style={{ flex: '3' }}>
                            <label style={labelStyle}>ESTADO CIVIL</label>
                            <div style={inputContainerStyle}>
                                <input type="text" value={localData.estadoCivil} onChange={handleChange('estadoCivil')} style={editableInputStyle} />
                            </div>
                        </div>
                    </div>

                    {/* Row 4: Sexo */}
                    <div style={{ ...fieldGroupStyle, marginTop: '5px' }}>
                        <label style={labelStyle}>SEXO</label>
                        <div style={{ display: 'flex', gap: '20px', alignItems: 'center', paddingLeft: '10px' }}>
                            {/* Masculino */}
                            <div 
                                onClick={handleSexoChange('M')}
                                style={{ display: 'flex', alignItems: 'center', gap: '8px', border: `2px solid ${themeColor}`, borderRadius: '20px', padding: '4px 12px', cursor: 'pointer' }}
                            >
                                <i className="bi bi-person-standing" style={{ color: '#7bc1f2', fontSize: '24px', lineHeight: 1 }}></i>
                                <div style={{ 
                                    width: '16px', 
                                    height: '16px', 
                                    borderRadius: '50%', 
                                    backgroundColor: localData.sexo === 'M' ? '#7bc1f2' : 'transparent',
                                    border: localData.sexo === 'M' ? 'none' : `2px solid ${themeColor}`
                                }}></div>
                            </div>
                            
                            {/* Femenino */}
                            <div 
                                onClick={handleSexoChange('F')}
                                style={{ display: 'flex', alignItems: 'center', gap: '8px', border: `2px solid ${themeColor}`, borderRadius: '20px', padding: '4px 12px', cursor: 'pointer' }}
                            >
                                <i className="bi bi-person-standing-dress" style={{ color: '#ff66cc', fontSize: '24px', lineHeight: 1 }}></i>
                                <div style={{ 
                                    width: '16px', 
                                    height: '16px', 
                                    borderRadius: '50%', 
                                    backgroundColor: localData.sexo === 'F' ? '#ff66cc' : 'transparent',
                                    border: localData.sexo === 'F' ? 'none' : `2px solid ${themeColor}`
                                }}></div>
                            </div>
                        </div>
                    </div>

                    {/* Row 5 */}
                    <div style={{ display: 'flex', gap: '15px', ...fieldGroupStyle }}>
                        <div style={{ flex: '5' }}>
                            <label style={labelStyle}>PROFESIÓN</label>
                            <div style={inputContainerStyle}>
                                <input type="text" value={localData.profesion} onChange={handleChange('profesion')} style={editableInputStyle} />
                            </div>
                        </div>
                        <div style={{ flex: '5' }}>
                            <label style={labelStyle}>DIRECCIÓN DE TRABAJO</label>
                            <div style={inputContainerStyle}>
                                <input type="text" value={localData.direccionTrabajo} onChange={handleChange('direccionTrabajo')} style={editableInputStyle} />
                            </div>
                        </div>
                    </div>

                    {/* Row 6 */}
                    <div style={{ ...fieldGroupStyle }}>
                        <label style={labelStyle}>DIRECCIÓN DE DOMICILIO</label>
                        <div style={inputContainerStyle}>
                            <input type="text" value={localData.direccionDomicilio} onChange={handleChange('direccionDomicilio')} style={editableInputStyle} />
                        </div>
                    </div>

                    {/* Row 7 */}
                    <div style={{ ...fieldGroupStyle }}>
                        <label style={labelStyle}>CORREO ELECTRÓNICO</label>
                        <div style={inputContainerStyle}>
                            <input type="email" value={localData.correo} onChange={handleChange('correo')} style={editableInputStyle} />
                        </div>
                    </div>

                    {/* Row 8 */}
                    <div style={{ ...fieldGroupStyle }}>
                        <label style={labelStyle}>OBSERVACIONES</label>
                        <div style={textAreaContainerStyle}>
                            <textarea value={localData.observaciones} onChange={handleChange('observaciones')} style={{...editableInputStyle, resize: 'none', height: '100%', minHeight: '64px'}} />
                        </div>
                    </div>

                </div>

                <div style={{ position: 'absolute', bottom: '0', left: '0', width: '100%', height: '25px', backgroundColor: '#333' }}></div>
            </div>
        </div>
    );
};

export default VisorHistoriaClinica;
